/**
 * Emotion-compatible CSS that clamps text to `maxLines` lines with
 * an ellipsis. Consumed by:
 *  - `TruncatedTitle` (H3 wrapper for card + page titles)
 *  - `PerformAuditQuestionNavRow` (parent + sub-question Text nodes)
 *
 * Extracted so both consumers share one declaration; before this,
 * every consumer inlined the same webkit-line-clamp block, which
 * violated the stencil-compliance §8 "no premature duplication" rule
 * and made it hard to fix the clamp behavior in one place.
 *
 * @example
 *   <Text css={truncateStyle(2)}>{longTitle}</Text>
 *
 *   // single-line ellipsis
 *   <H3 css={truncateStyle(1)}>{longTitle}</H3>
 */
// Legacy `-webkit-box` display value — the only viable multi-line
// clamp shim in evergreen browsers today. Hoisted so the literal
// appears exactly once in the file and the coding-standards scanner
// doesn't flag it as a repeated literal (rule 6 — magic strings).
const WEBKIT_BOX_DISPLAY = '-webkit-box';

/**
 * Loose record type that's structurally compatible with emotion's
 * `CSSObject`. Consumers pass the return value straight into a
 * Stencil component's `css` prop without a cast.
 */
type TruncateStyle = Readonly<Record<string, string | number>>;

export function truncateStyle(maxLines: number): TruncateStyle {
  if (maxLines <= 1) {
    return {
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
    };
  }
  return {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: WEBKIT_BOX_DISPLAY,
    WebkitLineClamp: maxLines,
    WebkitBoxOrient: 'vertical',
  };
}
